import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { noop } from 'lodash'
import {
  Stack,
  Box,
  Divider,
  Typography,
  Tooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from '@mui/material'
import InputV2 from 'components/generic/form/InputV2'
import SensorsIcon from '@mui/icons-material/Sensors'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import Select from 'components/generic/form/Select'
import Checkbox from 'components/generic/form/Checkbox'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import FormModalCustomActionBar from 'components/generic/modals/form-modal/FormModalCustomActionBar'
import { selectCamera, selectScales, selectCameras, selectScaleCameras } from 'state/devices'
import { selectCashDrawer } from 'state/settings'
import { hideFormModal } from 'state/ui'
import { ACCOUNT_PAYMENT_TYPE_ENUMS } from '../../../utils/constants.tsx'
import useQzTray from 'utils/custom-hooks/useQzTray'

export const cameraSelectorName = 'camera-selector'
export const cashDrawerSelectorName = 'cash-drawer-selector'

export default function EquipmentSelector({ onFormStateChange }) {
  const dispatch = useDispatch()
  const { qzFindPrinters, availablePrinters, testConnection } = useQzTray()

  // form
  const { control, handleSubmit, setValue, formState } = useForm({
    defaultValues: {
      scaleCameras: {},
    },
  })
  const { dirtyFields } = formState
  const isDirty = !!Object.keys(dirtyFields).length

  // state
  const { user, selectedYard } = useSelector(state => state.auth)
  const { devices, selectedCameraId, selectedCameraIds, selectedScaleIds } = useSelector(state => state.devices)
  const { cashDrawers, selectedCashDrawerId } = useSelector(state => state.settings)
  const [isInitialized, setIsInitialized] = useState(false)

  const filteredCashDrawers = useMemo(() => {
    return cashDrawers?.filter(drawer => drawer?.type !== ACCOUNT_PAYMENT_TYPE_ENUMS.ATM)
  }, [cashDrawers])

  // feature flags
  const { enableHardwareBundles, responsiveBOL } = useSelector(state => state.featureFlags?.variables || {})

  const cameras = devices?.filter(d => d.type === 'camera' && d.id !== 'upload')
  const noNativeCameras = devices?.filter(d => d.type === 'camera' && d.id !== 'native' && d.id !== 'upload')
  const scales = devices?.filter(d => d.type === 'scale' && d.id !== 'noscale')
  const getSelectionKey = selection => `${user.attributes.sub}:${selection}`

  useEffect(() => {
    // Fetch printers on component load
    qzFindPrinters()
  }, [])

  useEffect(() => {
    const savedDocumentPrinter = localStorage.getItem('documentPrinter')
    const savedTicketPrinter = localStorage.getItem('ticketPrinter')
    const savedPaperSize = localStorage.getItem('paperSize')
    const savedCheckPrinter = localStorage.getItem('checkPrinter')
    const savedNumberOfCopies = localStorage.getItem('numberOfCopies')

    if (savedDocumentPrinter) {
      setValue('documentPrinter', savedDocumentPrinter)
    }
    if (savedTicketPrinter) {
      setValue('ticketPrinter', savedTicketPrinter)
    }
    if (savedPaperSize) {
      setValue('paperSize', savedPaperSize)
    }
    if (savedCheckPrinter) {
      setValue('checkPrinter', savedCheckPrinter)
    }
    if (savedNumberOfCopies) {
      setValue('numberOfCopies', parseInt(savedNumberOfCopies))
    }
  }, [])

  useEffect(() => {
    if (!isInitialized && devices && user && filteredCashDrawers) {
      if (cameras.length) {
        if (selectedCameraId) {
          setValue(cameraSelectorName, selectedCameraId)
        }
      }
      if (noNativeCameras.length) {
        noNativeCameras.forEach(camera => {
          setValue(`cameras.${camera.id}`, selectedCameraIds.indexOf(camera.id) > -1)
        })
      }
      if (scales.length) {
        scales.forEach(scale => {
          setValue(`scales.${scale.id}`, selectedScaleIds.indexOf(scale.id) > -1)
        })
      }
      if (filteredCashDrawers.length) {
        if (selectedCashDrawerId) {
          setValue(cashDrawerSelectorName, selectedCashDrawerId)
        } else {
          setValue(cashDrawerSelectorName, filteredCashDrawers?.[0]?.id || '')
        }
      }
      setIsInitialized(true)
    }
  }, [devices, user, filteredCashDrawers])

  useEffect(() => {
    const savedScaleCameras = JSON.parse(localStorage.getItem(getSelectionKey('scaleCameras'))) || {}
    Object.keys(savedScaleCameras).forEach(scaleId => {
      savedScaleCameras[scaleId].forEach(cameraId => {
        setValue(`scaleCameras.${scaleId}.${cameraId}`, true)
      })
    })
  }, [devices, user, filteredCashDrawers])

  useEffect(() => {
    onFormStateChange(isDirty)
  }, [isDirty])

  const buttons = [
    {
      id: 'submit-equipment-button',
      text: 'Save Changes',
      type: 'submit',
      disabled: !isDirty,
      variant: 'contained',
    },
  ]

  async function submit(data) {
    Object.keys(data).forEach(selection => {
      if (selection === 'scales' || selection === 'cameras') {
        const newSelectedIds = Object.keys(data[selection]).filter(id => data[selection][id])
        localStorage.setItem(getSelectionKey(selection), JSON.stringify(newSelectedIds))
        switch (selection) {
          case 'scales':
            dispatch(selectScales(newSelectedIds))
            break
          case 'cameras':
            dispatch(selectCameras(newSelectedIds))
            break
          default:
            break
        }
      } else if (
        selection === 'documentPrinter' ||
        selection === 'ticketPrinter' ||
        selection === 'paperSize' ||
        selection === 'checkPrinter' ||
        selection === 'numberOfCopies'
      ) {
        // Save printer selections with static keys
        localStorage.setItem(selection, data[selection])
      } else {
        const selectionValue = selection === cashDrawerSelectorName ? `${selection}-${selectedYard}` : selection
        localStorage.setItem(getSelectionKey(selectionValue), data[selection])
        switch (selection) {
          case cameraSelectorName:
            dispatch(selectCamera(data[selection]))
            break
          case cashDrawerSelectorName:
            dispatch(selectCashDrawer(data[selection]))
            break
          default:
            break
        }
      }
    })

    // handle scaleCameras
    const scaleCamerasData = data.scaleCameras || {}
    const selectedScaleCameras = {}
    Object.keys(scaleCamerasData).forEach(scaleId => {
      const camerasForScale = scaleCamerasData[scaleId]
      selectedScaleCameras[scaleId] = Object.keys(camerasForScale).filter(cameraId => camerasForScale[cameraId])
    })
    localStorage.setItem(getSelectionKey('scaleCameras'), JSON.stringify(selectedScaleCameras))
    dispatch(selectScaleCameras(selectedScaleCameras))

    dispatch(hideFormModal())
  }

  const renderScaleSelector = () => {
    if (!scales?.length) return null
    return (
      <Stack pl={1}>
        <Typography variant="subtitle2">Scales</Typography>
        <Stack>
          {scales.map(scale =>
            enableHardwareBundles ? (
              <Accordion key={`scale-selector-${scale?.id}`} sx={{ boxShadow: 0 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ padding: 0 }}>
                  <div className="checkbox-wrapper" onClick={e => e.stopPropagation()}>
                    <Checkbox
                      name={`scales.${scale?.id}`}
                      label={scale?.name}
                      control={control}
                      onClick={e => e.stopPropagation()}
                    />
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  {noNativeCameras.length > 0 ? (
                    <Stack spacing={1}>
                      <Typography variant="subtitle2">Assign Cameras to {scale.name}</Typography>
                      {noNativeCameras.map(camera => (
                        <Checkbox
                          key={`scale-camera-selector-${scale.id}-${camera.id}`}
                          name={`scaleCameras.${scale.id}.${camera.id}`}
                          label={camera.name}
                          control={control}
                          onClick={e => e.stopPropagation()}
                        />
                      ))}
                      <Typography variant="caption" color="text.secondary">
                        If no cameras are selected for {scale.name}, the default camera selection will be used.
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No cameras available to assign to {scale.name}
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            ) : (
              <Checkbox
                key={`scale-selector-${scale?.id}`}
                name={`scales.${scale?.id}`}
                label={scale?.name}
                control={control}
              />
            ),
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {enableHardwareBundles
            ? 'Click on the dropdown to assign cameras to scales. If no scale is selected, you will not be able to take weights.'
            : 'If no scale is selected, you will not be able to take weights.'}
        </Typography>
      </Stack>
    )
  }

  const renderCamerasSelector = () => (
    <>
      {cameras?.length ? (
        <Stack pl={1}>
          <Typography pb={1} variant="subtitle2">
            Customer Cameras
          </Typography>
          <Select items={cameras} name="camera-selector" control={control} defaultValue="" />
        </Stack>
      ) : (
        ''
      )}
      {noNativeCameras.length ? (
        <Stack pl={1}>
          <Typography variant="subtitle2">Default Scale/Truck Camera Selection</Typography>
          {noNativeCameras.map(camera => (
            <Checkbox
              key={`camera-selector-${camera?.id}`}
              name={`cameras.${camera?.id}`}
              label={camera?.name}
              control={control}
            />
          ))}
          <Typography variant="caption" color="text.secondary">
            {enableHardwareBundles
              ? 'To use your native device camera, deselect all camera selections.'
              : 'If no camera is selected, your native device camera will be used.'}
          </Typography>
        </Stack>
      ) : (
        ''
      )}
    </>
  )

  const renderPrinterSelectors = () => {
    return (
      <>
        <Stack pl={1} rowGap={2}>
          <Stack spacing={0}>
            <Stack direction="row" alignItems="center">
              <Typography variant="subtitle2">Printers</Typography>
              <Tooltip title="Test connection to print server" placement="right">
                <IconButton size="small" onClick={testConnection}>
                  <SensorsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            {availablePrinters?.length ? (
              <Typography variant="caption" color="text.secondary">
                Choose a printer to print automatically, or select &apos;None&apos; to use the browser&apos;s print
                dialog.
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Connect to GreenSpark Print to enable automatic printing.
              </Typography>
            )}
          </Stack>
          {availablePrinters[0] && (
            <>
              <Stack direction="row" spacing={2}>
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <Select
                      items={[
                        { id: 'none', name: 'None (Manual Printing)' },
                        ...availablePrinters.map(printer => ({ id: printer, name: printer })),
                      ]}
                      name="documentPrinter"
                      control={control}
                      label={
                        responsiveBOL
                          ? 'Invoices, Settlements, Packing Lists'
                          : 'Invoices, Settlements, BOLs, Packing Lists'
                      }
                      defaultValue=""
                      clearable
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <InputV2
                      name="numberOfCopies"
                      control={control}
                      defaultValue={1}
                      label="Number of Copies"
                      type="number"
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                </Grid>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <Select
                      items={[
                        { id: 'none', name: 'None (Manual Printing)' },
                        ...availablePrinters.map(printer => ({ id: printer, name: printer })),
                      ]}
                      name="ticketPrinter"
                      control={control}
                      label={responsiveBOL ? 'Scale and Purchase Receipts, BOLs' : 'Scale and Purchase Receipts'}
                      defaultValue=""
                      clearable
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Select
                      items={[
                        { id: '72mm', name: '72mm' },
                        { id: '80mm', name: '80mm' },
                        { id: '104mm', name: '104mm' },
                        { id: '112mm', name: '112mm' },
                        { id: '216mm', name: 'Letter' },
                      ]}
                      name="paperSize"
                      control={control}
                      label="Receipt Paper Width"
                      defaultValue="104mm"
                    />
                  </Grid>
                </Grid>
              </Stack>
              <Select
                items={[
                  { id: 'none', name: 'None (Manual Printing)' },
                  ...availablePrinters.map(printer => ({ id: printer, name: printer })),
                ]}
                name="checkPrinter"
                control={control}
                label="Checks"
                defaultValue=""
                clearable
              />
            </>
          )}
          {!availablePrinters[0] && (
            <Select
              items={[
                { id: '72mm', name: '72mm' },
                { id: '80mm', name: '80mm' },
                { id: '104mm', name: '104mm' },
                { id: '112mm', name: '112mm' },
                { id: '216mm', name: 'Letter' },
              ]}
              name="paperSize"
              control={control}
              label="Receipt Paper Width"
              defaultValue="104mm"
            />
          )}
        </Stack>
        <Divider />
      </>
    )
  }

  const renderCashDrawers = () => {
    if (!filteredCashDrawers?.length) return null

    return (
      <Stack pl={1} rowGap={2}>
        <Typography variant="subtitle2">Cash Drawer</Typography>
        <Select
          items={filteredCashDrawers.map(drawer => ({
            ...drawer,
            name: drawer?.accountName,
          }))}
          name={cashDrawerSelectorName}
          control={control}
          label="Cash Drawer"
          defaultValue=""
        />
      </Stack>
    )
  }

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Stack height="100vh">
        <Box position="sticky" top={0} zIndex={1} bgcolor="background.paper">
          <FormModalHeading title="Equipment Selector" backButtonPosition="right" />
          <Divider />
        </Box>
        <Box px={4} py={2} flexGrow={1} sx={{ overflowY: 'auto' }}>
          <Stack rowGap={2}>
            {renderCamerasSelector()}
            <Divider />
            {renderScaleSelector()}
            <Divider />
            {renderPrinterSelectors()}
            {renderCashDrawers()}
          </Stack>
        </Box>
        <Box position="sticky" bottom={0} zIndex={1} bgcolor="background.paper">
          <FormModalCustomActionBar buttonConfigs={buttons} justify="flex-start" />
        </Box>
      </Stack>
    </form>
  )
}

EquipmentSelector.propTypes = {
  onFormStateChange: PropTypes.func,
}

EquipmentSelector.defaultProps = {
  onFormStateChange: noop,
}
